import axios from 'axios';
import { mutate } from 'swr';
import { withQueryParams } from '@shared/commonFunctions';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { getError } from '@shared/utils/helpers';
import {
  IPurpose,
  ICustomPreference,
  IPurposeOption,
  IConsentPreferenceTypeItem,
} from './components/Contact/components/OptOutModal/interfaces';

const assetPathV2 = `/${SERVICES.ASSET}/V2/assets`;

export const radioOptions = [
  { value: 'Pending', label: 'Pending' },
  { value: 'Approved', label: 'Approve' },
  { value: 'ApprovedCondition', label: 'Approve Condition' },
  { value: 'Denied', label: 'Deny' },
];

export interface IVehicleTitleInfoData {
  stateCode: string | undefined | null;
  userId: string;
  vehicleServiceType: string;
  caseNumber: string;
  make: string;
  year: string;
  requesterCode: string;
  dealCoValue?: number;
  ncicCode: string;
  reusePulledDataConfirmed?: boolean;
}
interface IContactConsentOptOutData {
  checkContactEmail: boolean;
  checkContactText: boolean;
}

export const getStatus = (status: string) => {
  const name = radioOptions
    .filter((option) => option.value === status)
    .map((n) => {
      return n.label;
    });

  return status ? name : null;
};

export const pullRegistrationInfo = (
  vinNumber: string,
  assetId: string,
  dealId: string,
  postData: IVehicleTitleInfoData,
  transactionId = ''
) => {
  const url = withQueryParams(`${assetPathV2}/${vinNumber}/registration`, { assetId, dealId, transactionId }, false);
  return mutate(url, async () => {
    try {
      const response = await axios.post(url, postData);
      return response.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestPullVehicleTitleInfo = (
  vin: string,
  assetId: string,
  postData: IVehicleTitleInfoData,
  transactionId = ''
) => {
  const url = withQueryParams(`${assetPathV2}/${assetId}/title`, { vinNumber: vin, transactionId }, false);

  return mutate(url, async () => {
    try {
      const response = await axios.post(url, postData);
      return response.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const transformConsentData = (consentPreferencesPurposeData: IPurpose[]) => {
  const consentOptions = [
    {
      name: 'Call',
      isConsented: false,
    },
    {
      name: 'SMS',
      isConsented: false,
    },
    {
      name: 'Email',
      isConsented: false,
    },
  ];

  return consentPreferencesPurposeData.map((purpose: IPurpose) => {
    const defaultOptions = purpose.name === 'Marketing' ? consentOptions.filter((_, i) => i !== 0) : consentOptions;
    const updatedPreferences: ICustomPreference[] = purpose.customPreferences.map((pref: ICustomPreference) => {
      const missingOptions = defaultOptions.filter((option: IPurposeOption) => {
        return !pref?.options?.some((prefOption) => prefOption.name === option.name);
      });

      return {
        ...pref,
        options: [...(pref.options === undefined ? [] : pref.options), ...missingOptions],
      };
    });
    if (purpose.customPreferences?.length === 0 &&  purpose.name !== 'RAMP' ) {
      return {
        ...purpose,
        customPreferences: [{ name: 'Preferences', options: [...defaultOptions] }],
      };
    }
    return {
      ...purpose,
      customPreferences: updatedPreferences,
    };
  });
};

export const tranformPreferences = (consentPreferencesDataTransformed: IPurpose[]) => {
  const transformedResult: IConsentPreferenceTypeItem[] = consentPreferencesDataTransformed?.flatMap((purpose: IPurpose) => {
    const mainPurpose: IConsentPreferenceTypeItem = {
      consentPreferenceName: purpose.name,
      consentPreferenceLabel: purpose.label,
      isOptedIn: purpose.status === 'ACTIVE', // Set isOptedIn based on status
    };

    const customPreferences = purpose?.customPreferences?.flatMap((pref: ICustomPreference) => {
      return (
        pref?.options?.map((option: IPurposeOption) => {
          const optionResult: IConsentPreferenceTypeItem = {
            consentPreferenceName: `${purpose?.name}_${option?.name}`,
            consentPreferenceLabel: option?.name,
            isOptedIn: option?.isConsented,
          };
          return optionResult;
        }) ?? []
      );
    });

    return [mainPurpose, ...customPreferences];
  });

  return transformedResult;
};

export const getMappedContactConsentOptOutData = (optOutItems: IConsentPreferenceTypeItem[]): IContactConsentOptOutData => {
  const result: IContactConsentOptOutData = optOutItems.reduce(
    (acc, cur) => {
      if (cur.consentPreferenceName === 'Application_Email') {
        acc = {
          ...acc,
          checkContactEmail: cur.isOptedIn,
        };
      }

      if (cur.consentPreferenceName === 'Application_SMS') {
        acc = {
          ...acc,
          checkContactText: cur.isOptedIn,
        };
      }

      return acc;
    },
    {
      checkContactEmail: false,
      checkContactText: false,
    } as IContactConsentOptOutData
  );

  return result;
};
